// ============================================================================
// FNE-BRIDGE - Store Paramètres (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { Setting, Client, Product, Notification } from '../types/models';
import type { SettingCategory } from '../types/enums';

// ============================================================================
// Types du state
// ============================================================================

interface SettingsState {
  // Paramètres
  settings: Setting[];
  settingsLoading: boolean;
  settingsError: string | null;
  settingsSaving: boolean;

  // Clients/Fournisseurs
  clients: Client[];
  clientsTotal: number;
  clientsLoading: boolean;

  // Articles
  products: Product[];
  productsTotal: number;
  productsLoading: boolean;

  // Notifications
  notifications: Notification[];
  unreadCount: number;
  notificationsLoading: boolean;

  // Actions paramètres
  setSettings: (settings: Setting[]) => void;
  setSettingsLoading: (loading: boolean) => void;
  setSettingsError: (error: string | null) => void;
  setSettingsSaving: (saving: boolean) => void;
  updateSetting: (cle: string, valeur: unknown) => void;
  getSettingValue: (cle: string, defaultValue?: unknown) => unknown;
  getSettingsByCategory: (category: SettingCategory) => Setting[];

  // Actions clients
  setClients: (clients: Client[], total: number) => void;
  setClientsLoading: (loading: boolean) => void;
  addClient: (client: Client) => void;
  updateClient: (id: string, updates: Partial<Client>) => void;
  removeClient: (id: string) => void;

  // Actions articles
  setProducts: (products: Product[], total: number) => void;
  setProductsLoading: (loading: boolean) => void;
  addProduct: (product: Product) => void;
  updateProduct: (id: string, updates: Partial<Product>) => void;
  removeProduct: (id: string) => void;

  // Actions notifications
  setNotifications: (notifications: Notification[]) => void;
  setNotificationsLoading: (loading: boolean) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Notification) => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  settings: [],
  settingsLoading: false,
  settingsError: null,
  settingsSaving: false,
  clients: [],
  clientsTotal: 0,
  clientsLoading: false,
  products: [],
  productsTotal: 0,
  productsLoading: false,
  notifications: [],
  unreadCount: 0,
  notificationsLoading: false,

  // --- Paramètres ---
  setSettings: (settings) => set({ settings, settingsLoading: false }),
  setSettingsLoading: (settingsLoading) => set({ settingsLoading }),
  setSettingsError: (settingsError) => set({ settingsError, settingsLoading: false }),
  setSettingsSaving: (settingsSaving) => set({ settingsSaving }),

  updateSetting: (cle, valeur) =>
    set((state) => ({
      settings: state.settings.map((s) =>
        s.cle === cle ? { ...s, valeur, modifie_le: new Date().toISOString() } : s
      ),
    })),

  getSettingValue: (cle, defaultValue = null) => {
    const setting = get().settings.find((s) => s.cle === cle);
    return setting ? setting.valeur : defaultValue;
  },

  getSettingsByCategory: (category) => {
    return get().settings.filter((s) => s.categorie === category);
  },

  // --- Clients ---
  setClients: (clients, clientsTotal) => set({ clients, clientsTotal, clientsLoading: false }),
  setClientsLoading: (clientsLoading) => set({ clientsLoading }),

  addClient: (client) =>
    set((state) => ({
      clients: [client, ...state.clients],
      clientsTotal: state.clientsTotal + 1,
    })),

  updateClient: (id, updates) =>
    set((state) => ({
      clients: state.clients.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    })),

  removeClient: (id) =>
    set((state) => ({
      clients: state.clients.filter((c) => c.id !== id),
      clientsTotal: state.clientsTotal - 1,
    })),

  // --- Articles ---
  setProducts: (products, productsTotal) => set({ products, productsTotal, productsLoading: false }),
  setProductsLoading: (productsLoading) => set({ productsLoading }),

  addProduct: (product) =>
    set((state) => ({
      products: [product, ...state.products],
      productsTotal: state.productsTotal + 1,
    })),

  updateProduct: (id, updates) =>
    set((state) => ({
      products: state.products.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),

  removeProduct: (id) =>
    set((state) => ({
      products: state.products.filter((p) => p.id !== id),
      productsTotal: state.productsTotal - 1,
    })),

  // --- Notifications ---
  setNotifications: (notifications) =>
    set({
      notifications,
      unreadCount: notifications.filter((n) => !n.est_lue).length,
      notificationsLoading: false,
    }),

  setNotificationsLoading: (notificationsLoading) => set({ notificationsLoading }),

  markAsRead: (id) =>
    set((state) => {
      const updated = state.notifications.map((n) =>
        n.id === id ? { ...n, est_lue: true, lue_le: new Date().toISOString() } : n
      );
      return {
        notifications: updated,
        unreadCount: updated.filter((n) => !n.est_lue).length,
      };
    }),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        est_lue: true,
        lue_le: n.lue_le || new Date().toISOString(),
      })),
      unreadCount: 0,
    })),

  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + (notification.est_lue ? 0 : 1),
    })),

  // --- Reset ---
  reset: () =>
    set({
      settings: [],
      settingsLoading: false,
      settingsError: null,
      settingsSaving: false,
      clients: [],
      clientsTotal: 0,
      clientsLoading: false,
      products: [],
      productsTotal: 0,
      productsLoading: false,
      notifications: [],
      unreadCount: 0,
      notificationsLoading: false,
    }),
}));
